import { Stack, SxProps, Table, TableBody, TableCell, TableHead, TableRow, Theme, Typography, useTheme } from "@mui/material";
import { orderLinesSectionK, useOrderLinesSectionT } from "src/translation-hooks/order-lines-section";
import { useConfiguratorPageT } from "src/translation-hooks/configurator-page";
import { RootState } from "src/store";
import { connect } from "react-redux";
import { useCurrencyTranslation } from "src/translations-enums-hooks/useCurrencyTranslation";
import { Shared_Currencies } from "src/backend/openapi/v1";
import { PartsListLoading } from "./parts-list-loading";
import { thousandsSeparator } from "src/utils";
import { useUserSettings } from "src/hooks";

const mapStateToProps = (state: RootState) => ({
  generateConfigurationOrder: state.pageReducer.configureProductReducer.generateConfigurationOrder
});

type Props = ReturnType<typeof mapStateToProps>;

const PartsListBase = (props: Props) => {
  const { generateConfigurationOrder } = props;

  const theme = useTheme();
  const orderLinesSectionT = useOrderLinesSectionT();
  const configuratorPageT = useConfiguratorPageT();
  const { getCurrencyTranslation } = useCurrencyTranslation();
  const { language } = useUserSettings();
  const locale = language === "da" ? "da-dk" : "en";

  const formatPrice = (price: number | null | undefined) => (price === null || price === undefined ? "" : thousandsSeparator(price, locale));

  const headerCells = [
    { key: orderLinesSectionK.product, label: orderLinesSectionT.product },
    { key: orderLinesSectionK.productNumber, label: orderLinesSectionT.productNumber },
    { key: orderLinesSectionK.currency, label: orderLinesSectionT.currency },
    { key: orderLinesSectionK.quantity, label: orderLinesSectionT.quantity },
    { key: orderLinesSectionK.unitOfMeasure, label: orderLinesSectionT.unitOfMeasure },
    { key: orderLinesSectionK.salesPrice, label: orderLinesSectionT.salesPrice },
    { key: orderLinesSectionK.productionPrice, label: orderLinesSectionT.productionPrice }
  ];

  const tableSx: SxProps<Theme> = {
    color: theme.palette.customColors.black
  };

  if (generateConfigurationOrder.loading === "not-started" || generateConfigurationOrder.loading === "is-loading") {
    return <PartsListLoading />;
  }

  // a failed request falls through to the empty-state row below — the error itself is surfaced by the global error viewer
  const lines = generateConfigurationOrder.loading === "has-loaded" ? (generateConfigurationOrder.data.lines ?? []) : [];

  return (
    <Stack overflow="hidden" p={4} gap={1}>
      <Typography variant="h2" fontWeight="normal" mb={2} color={theme.palette.customColors.darkGrey}>
        {configuratorPageT.partsList}
      </Typography>
      <Stack overflow="auto" bgcolor={theme.palette.customColors.white} border={`1px solid ${theme.palette.customColors.grey}`} borderRadius={1}>
        <Table size="small">
          <TableHead sx={{ position: "sticky", top: 0, bgcolor: theme.palette.customColors.white, borderBottom: `2px solid ${theme.palette.customColors.border}` }}>
            <TableRow>
              {headerCells.map(({ key, label }) => (
                <TableCell key={key} sx={{ border: "none", pb: 1 }}>
                  <Typography variant="subtitle1" sx={{ color: theme.palette.customColors.black }}>
                    {label}
                  </Typography>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody sx={{ borderTop: `1px solid ${theme.palette.customColors.grey}` }}>
            {lines.length > 0 ? (
              <>
                {lines.map(line => (
                  <TableRow key={line.systemProductConfigurationItemId} sx={{ borderBottom: `1px solid ${theme.palette.customColors.border}` }}>
                    <TableCell sx={tableSx}>
                      <Typography variant="subtitle1">{line.description}</Typography>
                    </TableCell>
                    <TableCell sx={tableSx}>
                      <Typography variant="subtitle1">{line.productNumber}</Typography>
                    </TableCell>
                    <TableCell sx={tableSx}>
                      <Typography variant="subtitle1">{line.currency ? getCurrencyTranslation(line.currency as Shared_Currencies) : ""}</Typography>
                    </TableCell>
                    <TableCell sx={tableSx}>
                      <Typography variant="subtitle1">{line.quantity}</Typography>
                    </TableCell>
                    <TableCell sx={tableSx}>
                      <Typography variant="subtitle1">{line.unitOfMeasure}</Typography>
                    </TableCell>
                    <TableCell sx={{ ...tableSx, textAlign: "end" }}>
                      <Typography variant="subtitle1">{formatPrice(line.salesPrice)}</Typography>
                    </TableCell>
                    <TableCell sx={{ ...tableSx, textAlign: "end" }}>
                      <Typography variant="subtitle1">{formatPrice(line.productionPrice)}</Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </>
            ) : (
              <TableRow>
                <TableCell sx={{ py: 2, textAlign: "center" }} colSpan={headerCells.length}>
                  <Typography>{orderLinesSectionT.noProducts}</Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Stack>
    </Stack>
  );
};

export const PartsList = connect(mapStateToProps, undefined)(PartsListBase);
